import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { ProgressBar } from "primereact/progressbar";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useApiPagination, useDebounce, useToast } from "sj-ui-toolkit/src/hooks";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { downloadCsv } from "sj-ui-toolkit/src/utils/downloadFile";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";

import type {
  AssignmentReportRow,
  AssignmentsReportResponse,
  AssignmentsReportFilterOptions,
  AssignmentReportFilters,
} from "@/api/assignmentsReport";
import {
  loadAssignmentsReportPage,
  downloadAssignmentsReportCsv,
  loadAssignmentsReportFilterOptions,
} from "@/api/assignmentsReport";
import {
  loadAssignmentsReportPlaMetaRow,
  type PersonalizedLessonAssignmentMetaRow,
} from "@/api/personalizedLessonAssignments";
import {
  ASSIGNMENT_STATUS_CONFIG,
  getAssignmentStatusKey,
} from "@/constants/assignmentsReport.constants";
import AssignmentsReportFilters from "@/components/reports/AssignmentsReportFilters";
import { withSkeleton, statusBody, dateBody } from "@/helpers/tableBody";
import { useAspenFeatures } from "@/hooks/useAspenFeatures";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SORT_FIELD_MAP: Record<string, string> = {
  firstName: "first_name",
  lastName: "last_name",
  name: "assignment",
  dueDate: "due_date",
  completedAt: "completed_at",
};

const toApiSortField = (field: string | null | undefined): string | null =>
  field ? (SORT_FIELD_MAP[field] ?? field) : null;

// ---------------------------------------------------------------------------
// Module-level column body templates
// ---------------------------------------------------------------------------

const emailBody = withSkeleton<AssignmentReportRow>(row => row.email, "14rem");
const firstNameBody = withSkeleton<AssignmentReportRow>(row => row.firstName ?? "--", "6rem");
const lastNameBody = withSkeleton<AssignmentReportRow>(row => row.lastName ?? "--", "6rem");
const assignmentNameBody = withSkeleton<AssignmentReportRow>(row => row.name ?? "--", "10rem");
const timeSpentBody = withSkeleton<AssignmentReportRow>(row => row.timeSpent ?? "\u2014", "5rem");
const dueDateBody = withSkeleton<AssignmentReportRow>(
  row => humanDateWithoutTime(row.dueDate),
  "5rem"
);
const completedAtBody = dateBody<AssignmentReportRow>("completedAt", "Incomplete");

const SIDEBAR_FILTER_KEYS: (keyof AssignmentReportFilters)[] = [
  "status",
  "jobRole",
  "title",
  "manager",
  "managersEmail",
  "employeeNumber",
  "securityChampion",
  "department",
  "businessUnit",
  "team",
  "company",
  "country",
  "assignment",
  "archived",
];

const buildApiParams = (
  f: AssignmentReportFilters,
  sortField: string | null | undefined,
  sortOrder: number | null | undefined
) => ({
  sortable_column: toApiSortField(sortField),
  order_type: (sortOrder === 1 ? "asc" : "desc") as "asc" | "desc",
  query: f.global?.value ?? undefined,
  from: f.dateRange?.value?.[0]
    ? Math.floor(new Date(f.dateRange.value[0]).getTime() / 1000)
    : undefined,
  to: f.dateRange?.value?.[1]
    ? Math.floor(new Date(f.dateRange.value[1]).getTime() / 1000)
    : undefined,
  status: f.status?.value?.length ? f.status.value : undefined,
  job_role: f.jobRole?.value?.length ? f.jobRole.value : undefined,
  title: f.title?.value?.length ? f.title.value : undefined,
  department: f.department?.value?.length ? f.department.value : undefined,
  business_unit: f.businessUnit?.value?.length ? f.businessUnit.value : undefined,
  country: f.country?.value?.length ? f.country.value : undefined,
  company: f.company?.value?.length ? f.company.value : undefined,
  manager: f.manager?.value?.length ? f.manager.value : undefined,
  security_champion: f.securityChampion?.value?.length ? f.securityChampion.value : undefined,
  team: f.team?.value?.length ? f.team.value : undefined,
  managers_email: f.managersEmail?.value?.length ? f.managersEmail.value : undefined,
  employee_number: f.employeeNumber?.value?.length ? f.employeeNumber.value : undefined,
  assignment: f.assignment?.value?.length ? f.assignment.value : undefined,
  archived: f.archived?.value?.length ? f.archived.value : undefined,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AssignmentsReport() {
  useBreadcrumb({
    label: "Assignment Progress Reporting",
    route: AdminAppRoutes.routes.reporting.routes.assignments.route,
    display: "none",
    resetTrail: true,
  });
  const hideTimeSpent = useHideTimeSpent();
  const { showToast } = useToast();
  const { currentTenantName } = useAspenFeatures({ enabled: true });

  // --- Pagination / data ---
  const { data, totalRecords, lazyState, onPage, onSort, onFilter } = useApiPagination({
    storageKey: "assignmentsReport",
    dataKey: "assignments",
    calculateTotalRecords: (res: AssignmentsReportResponse, pageSize: number) => {
      if (!res?.assignments) return 0;
      const total = res.totalPages ?? 0;
      if (total <= 1) return res.assignments.length;
      return (total - 1) * pageSize + res.assignments.length;
    },
    loadData: async ({ page, perPage, sortField, sortOrder, filters }) => {
      const f = (filters ?? {}) as AssignmentReportFilters;
      return await loadAssignmentsReportPage({
        page,
        per_page: perPage,
        ...buildApiParams(f, sortField, sortOrder),
      });
    },
  });

  // --- Local UI state ---
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [filterOptions, setFilterOptions] = useState<AssignmentsReportFilterOptions | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [plaMetaRow, setPlaMetaRow] = useState<PersonalizedLessonAssignmentMetaRow | null>(null);

  const [searchText, setSearchText] = useState<string>(
    () => ((lazyState.filters as AssignmentReportFilters)?.global?.value as string) ?? ""
  );
  const [dateRange, setDateRange] = useState<Date[] | null>(() => {
    const dr = (lazyState.filters as AssignmentReportFilters)?.dateRange?.value;
    if (!dr?.[0]) return null;
    return [new Date(dr[0]), dr[1] ? new Date(dr[1]) : new Date(dr[0])];
  });

  const debouncedSearch = useDebounce(searchText, 300);

  // Stable ref so effects always read latest filters without stale closures
  const filtersRef = useRef<AssignmentReportFilters>(
    (lazyState.filters as AssignmentReportFilters) ?? {}
  );
  useEffect(() => {
    filtersRef.current = (lazyState.filters as AssignmentReportFilters) ?? {};
  });

  // Skip first render to avoid clearing cache on mount
  const searchMounted = useRef(false);
  useEffect(() => {
    if (!searchMounted.current) {
      searchMounted.current = true;
      return;
    }
    onFilter({
      ...lazyState,
      filters: { ...filtersRef.current, global: { value: debouncedSearch || null } },
    });
  }, [debouncedSearch]);

  // Load filter options once on mount
  useEffect(() => {
    setLoadingOptions(true);
    loadAssignmentsReportFilterOptions()
      .then(setFilterOptions)
      .catch((err: unknown) => console.error("Failed to load filter options:", err))
      .finally(() => setLoadingOptions(false));
  }, []);

  useEffect(() => {
    if (!currentTenantName) return;

    const loadPlaMetaRow = async () => {
      try {
        setPlaMetaRow(
          await loadAssignmentsReportPlaMetaRow(
            currentTenantName,
            AdminAppRoutes.routes.reporting.routes.assignments.routes.personalizedLessonAssignments
              .route
          )
        );
      } catch (error) {
        console.error("Failed to load PLA meta row:", error);
      }
    };

    void loadPlaMetaRow();
  }, [currentTenantName]);

  // --- Derived ---
  const activeSidebarFilterCount = SIDEBAR_FILTER_KEYS.reduce((count, key) => {
    const entry = (lazyState.filters as Record<string, { value: string[] } | undefined>)?.[key];
    return count + (entry?.value?.length ? 1 : 0);
  }, 0);

  // --- Handlers ---
  const handleDateRangeChange = (dates: Date[] | null) => {
    setDateRange(dates);
    const [from, to] = dates ?? [];
    const complete = Array.isArray(dates) && dates.length === 2 && from && to;
    onFilter({
      ...lazyState,
      filters: {
        ...filtersRef.current,
        dateRange: {
          value: complete ? [from.toISOString(), to.toISOString()] : null,
        },
      },
    });
  };

  const handleFiltersApply = (updatedFilters: AssignmentReportFilters) => {
    onFilter({ ...lazyState, filters: { ...filtersRef.current, ...updatedFilters } });
    setSidebarVisible(false);
  };

  const handleClearAllFilters = () => {
    setSearchText("");
    setDateRange(null);
    onFilter({ ...lazyState, filters: {} });
  };

  const handleFiltersClear = () => {
    const { global, dateRange } = filtersRef.current;
    const retainedFilters: AssignmentReportFilters = {};
    if (global !== undefined) retainedFilters.global = global;
    if (dateRange !== undefined) retainedFilters.dateRange = dateRange;
    onFilter({ ...lazyState, filters: retainedFilters });
    setSidebarVisible(false);
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const csv = await downloadAssignmentsReportCsv(
        buildApiParams(filtersRef.current, lazyState.sortField, lazyState.sortOrder)
      );
      downloadCsv(csv, "assignments_report.csv");
    } catch (error) {
      console.error("Failed to export assignments report:", error);
      showToast({ severity: "error", detail: "Failed to export assignments report" });
    } finally {
      setIsExporting(false);
    }
  };

  const tableData = useMemo(() => {
    if (!plaMetaRow || lazyState.first !== 0) return data;
    return [plaMetaRow, ...data];
  }, [data, lazyState.first, plaMetaRow]);

  // --- Column bodies ---
  const assignmentStatusBody = statusBody<AssignmentReportRow>(row => {
    const key = getAssignmentStatusKey(row);
    const config = ASSIGNMENT_STATUS_CONFIG[key];
    return {
      icon: config.icon || undefined,
      colorClass: config.color ? `text-${config.color}` : undefined,
      label: row.status ?? "—",
    };
  });

  const progressBody = withSkeleton<AssignmentReportRow>(row => {
    const passed = row.progress?.passed ?? 0;
    const all = row.progress?.all ?? 0;
    const pct = all > 0 ? Math.round((passed / all) * 100) : 0;
    return (
      <div className="flex items-center gap-2">
        <ProgressBar value={pct} style={{ height: "0.5rem", width: "8rem" }} showValue={false} />
        <span>{pct}%</span>
      </div>
    );
  }, "8rem");

  const emailColumnBody = (row: AssignmentTableRow | null) => {
    if (row == null) return emailBody(row);
    if ("personalizedLessonAssignment" in row && row.personalizedLessonAssignment) {
      return (
        <Link
          to={row.personalizedLessonAssignmentPath}
          aria-label={`Open ${row.name} report`}
          className={`flex items-center gap-2 font-semibold whitespace-nowrap ${themeClasses.textPrimaryDarker}`}
        >
          <i className="pi pi-list-check" aria-hidden="true" />
          {row.name}
        </Link>
      );
    }

    return emailBody(row as AssignmentReportRow);
  };

  const firstNameColumnBody = (row: AssignmentTableRow | null) =>
    row != null && "personalizedLessonAssignment" in row
      ? null
      : firstNameBody((row ?? null) as AssignmentReportRow | null);

  const lastNameColumnBody = (row: AssignmentTableRow | null) =>
    row != null && "personalizedLessonAssignment" in row
      ? null
      : lastNameBody((row ?? null) as AssignmentReportRow | null);

  const assignmentNameColumnBody = (row: AssignmentTableRow | null) =>
    row != null && "personalizedLessonAssignment" in row
      ? null
      : assignmentNameBody((row ?? null) as AssignmentReportRow | null);

  const timeSpentColumnBody = (row: AssignmentTableRow | null) =>
    row != null && "personalizedLessonAssignment" in row
      ? null
      : timeSpentBody((row ?? null) as AssignmentReportRow | null);

  const dueDateColumnBody = (row: AssignmentTableRow | null) =>
    row != null && "personalizedLessonAssignment" in row
      ? null
      : dueDateBody((row ?? null) as AssignmentReportRow | null);

  const completedAtColumnBody = (row: AssignmentTableRow | null) =>
    row != null && "personalizedLessonAssignment" in row
      ? null
      : completedAtBody((row ?? null) as AssignmentReportRow | null);

  const statusColumnBody = (row: AssignmentTableRow | null) => {
    if (row == null) return assignmentStatusBody(row as AssignmentReportRow | null);
    if ("personalizedLessonAssignment" in row && row.personalizedLessonAssignment) {
      return (
        <div className="flex items-center gap-2">
          <span>{row.status || "Ongoing"}</span>
        </div>
      );
    }

    return assignmentStatusBody(row as AssignmentReportRow);
  };

  const progressColumnBody = (row: AssignmentTableRow | null) => {
    if (row == null) return progressBody(row as AssignmentReportRow | null);
    if ("personalizedLessonAssignment" in row && row.personalizedLessonAssignment) {
      const required = row.progress.required;
      const requiredPassed = row.progress.requiredPassed;
      const pct = required > 0 ? Math.round((requiredPassed / required) * 100) : 0;
      return (
        <div className="flex items-center gap-2">
          <ProgressBar value={pct} style={{ height: "0.5rem", width: "8rem" }} showValue={false} />
          <span>{pct}%</span>
        </div>
      );
    }

    return progressBody(row as AssignmentReportRow);
  };

  // --- Render ---
  return (
    <>
      <div className="mb-4">
        <h1>Assignment Progress Reporting</h1>
      </div>
      {/* Top bar */}
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex flex-wrap items-center gap-2">
          <IconField iconPosition="left" className="min-w-48">
            <InputIcon className="pi pi-search" />
            <InputText
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search assignments..."
              aria-label="Search assignments"
              data-testid="assignments-search-input"
              className="w-full"
            />
          </IconField>

          <Calendar
            value={dateRange}
            onChange={e => handleDateRangeChange(e.value as Date[] | null)}
            selectionMode="range"
            placeholder="Select date range"
            ariaLabel="Date range filter"
            showIcon
            readOnlyInput
            showButtonBar
          />
          <Button
            icon="pi pi-filter-slash"
            label="Clear Filters"
            size="small"
            severity="secondary"
            onClick={handleClearAllFilters}
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="relative inline-flex">
            <Button
              icon="pi pi-filter"
              label="Advanced Filters"
              severity="secondary"
              outlined
              onClick={() => setSidebarVisible(true)}
            />
            {activeSidebarFilterCount > 0 && (
              <Badge value={activeSidebarFilterCount} className="absolute -top-2 -right-2" />
            )}
          </div>

          <Button
            icon="pi pi-download"
            label="Export CSV"
            loading={isExporting}
            disabled={isExporting}
            data-testid="assignments-export-button"
            onClick={() => void handleExportCsv()}
          />
        </div>
      </div>
      {/* Table */}
      <DataTable
        value={tableData as unknown as AssignmentReportRow[]}
        lazy
        paginator
        data-testid="assignments-report-table"
        rows={lazyState.rows}
        first={lazyState.first}
        totalRecords={totalRecords}
        sortField={lazyState.sortField ?? undefined}
        sortOrder={lazyState.sortOrder as 1 | -1 | 0 | null | undefined}
        onPage={e => void onPage(e)}
        onSort={onSort}
        removableSort
        rowsPerPageOptions={[10, 25, 50, 100]}
        emptyMessage="No results found."
        tableClassName="dt-responsive-text"
      >
        <Column
          field="email"
          header="Email"
          sortable
          style={{ width: "350px" }}
          body={emailColumnBody}
        />
        <Column field="firstName" header="First" sortable body={firstNameColumnBody} />
        <Column field="lastName" header="Last" sortable body={lastNameColumnBody} />
        <Column field="name" header="Assignment Name" body={assignmentNameColumnBody} />
        {!hideTimeSpent && (
          <Column
            field="timeSpent"
            header="Time Spent"
            style={{ width: "150px" }}
            body={timeSpentColumnBody}
          />
        )}
        <Column
          field="dueDate"
          header="Due Date"
          sortable
          style={{ width: "150px" }}
          body={dueDateColumnBody}
        />
        <Column
          field="completedAt"
          header="Completion Date"
          sortable
          style={{ width: "200px" }}
          body={completedAtColumnBody}
        />
        <Column field="status" header="Status" sortable body={statusColumnBody} />
        <Column field="progress" header="Progress" sortable body={progressColumnBody} />
      </DataTable>

      {/* Filter sidebar */}
      <AssignmentsReportFilters
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        filterOptions={filterOptions}
        loadingOptions={loadingOptions}
        currentFilters={(lazyState.filters as AssignmentReportFilters) ?? {}}
        onApply={handleFiltersApply}
        onClear={handleFiltersClear}
      />
    </>
  );
}
type AssignmentTableRow = AssignmentReportRow | PersonalizedLessonAssignmentMetaRow;
